import React from "react";
import { LocaleUtil } from "@eres/core";
import Strings from "@my-dld/assets/strings";
import { extractPropInfo } from "../MyDLD/PropertyDetails";
import { AssetCard } from "@10x/dubai-rest-api/dist/property_registry/entity";
import BaseTabuRequestForm, {
	addAgentParticipantId,
	BaseTabuRequestProps,
	Lookup,
	RenderTabuRequiredDocuments,
	ReqTabuDoc,
	TabuDoc
} from "./BaseTabuRequestForm";
import {
	ITabuSmartService,
	OwnershipChangeRequest,
	TabuDocumentRequest,
	TabuLookUp,
	TabuLookUpData,
	TabuProcedures,
	TabuRequestApi,
	TabuUtils
} from "@10x/tabu-mobile-api";
import TextInputField from "@my-dld/components/Form/TextInputField";
import { hideLoading, showErrorMessage, showLoading } from "@my-dld/utils/UIUtils";
import { getParticipantID } from "@10x/sso";
import { Redirect } from "react-router";
import { DashboardBCItem } from "@my-dld/contexts/PageContext";
import PageTitle from "@my-dld/components/PageTitle";
import PageContent from "@my-dld/components/PageContent";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import { checkNonEmpty, ErrorContainerStateProvider } from "@my-dld/components/Form/FormField";
import { ErrorContainer } from "@eres/forms";
import { REQ_SOURCE_DLD_WEB } from "@eres/rtregistrationapi/entities";
import { ProcedureDetail, TabuRequestSubmitResponse } from "@10x/tabu-mobile-api/dist/requests/entity";
import { extractTabuRequestPropertyInfo } from "@my-dld/components/MyRequest/TabuRequestPropertyHeader";
import { TabuRequestContext } from "@my-dld/contexts/TabuRequestContext";
import { navigate } from "@my-dld/utils/StoreUtils";
import { getPropertyType, getUnitNumber, getVillaName, TabuPropertyType } from "@my-dld/utils/PropertyUtils";
import { getOPMunNo } from "@my-dld/components/Portfolio/PropertyTable";
import { isMultiPropertyProcedure } from "@my-dld/utils/ProcedureUtils";

export interface State {
	notes?: string;
	property?: AssetCard;
	properties?: AssetCard[];
	errorContainer: ErrorContainer;
	docs: ReqTabuDoc[];
	prevRequest?: ProcedureDetail;
}

function ToDocData( lookup: TabuLookUpData ): ReqTabuDoc {
	return {
		...lookup,
		identity: lookup.Identity,
	} as any as ReqTabuDoc;
}

class OwnershipChangeForm extends BaseTabuRequestForm<BaseTabuRequestProps, State> {
	render() {
		return <AbstractRemarksForm
			initialState={ this.initialState }
			documentLookupName={ this.documentLookupName }
			previousRequestId={ this.previousRequestId }
			previousDocs={ this.previousDocs }
			submitApi={ TabuRequestApi.submitOwnerShipChangeRequest }
			procedure={ TabuProcedures.OwnershipChange }
			{ ...this.props }/>;
	}

	initialState = ( previousRequest?: ProcedureDetail ) => {
		const oldRemark = previousRequest?.OwnChangeExtendedProperties?.Remarks;
		if ( oldRemark ) {
			return {
				notes: oldRemark,
			};
		}
		return {};
	};
	documentLookupName = () => {
		return TabuLookUp.OWNERSHIP_CHANGE_DOCUMENTS;
	};
	previousRequestId = ( previousRequest: ProcedureDetail ) => {
		return previousRequest?.OwnChangeExtendedProperties?.Identity ?? undefined;
	};
	previousDocs = ( previousRequest: ProcedureDetail ) => {
		return previousRequest?.OwnChangeExtendedProperties?.Documents ?? undefined;
	};
}

export class DelayedPropertyCutOffForm extends BaseTabuRequestForm<BaseTabuRequestProps, State> {
	render() {
		return <AbstractPropertyCutOffForm
			documentLookupName={ this.documentLookupName }
			submitApi={ TabuRequestApi.submitDelayedPropertyCutOffRequest }
			procedure={ TabuProcedures.DelayedPropertyCutOff }
			{ ...this.props }/>;
	}

	documentLookupName = () => {
		return TabuLookUp.DELAYED_CUT_OFF_DOCUMENTS;
	};
}

export class PropertyCutOffForm extends BaseTabuRequestForm<BaseTabuRequestProps, State> {
	render() {
		return <AbstractPropertyCutOffForm
			documentLookupName={ this.documentLookupName }
			submitApi={ TabuRequestApi.submitPropertyCutOffRequest }
			procedure={ TabuProcedures.PropertyCutOff }
			{ ...this.props }/>;
	}

	documentLookupName = () => {
		return TabuLookUp.CUT_OFF_DOCUMENTS;
	};
}

interface AbstractPropertyCutOffFormProps extends Partial<AbstractRemarksFormProps> {
	documentLookupName: () => string | undefined;
	procedure: ITabuSmartService;
	submitApi: <T extends OwnershipChangeRequest>( request: T ) => Promise<TabuRequestSubmitResponse>;
}

export class AbstractPropertyCutOffForm extends BaseTabuRequestForm
	<AbstractPropertyCutOffFormProps & BaseTabuRequestProps, State> {
	render() {
		return <AbstractRemarksForm
			initialState={ this.initialState }
			previousRequestId={ this.previousRequestId }
			previousDocs={ this.previousDocs }
			{ ...this.props }/>;
	}

	initialState = ( previousRequest?: ProcedureDetail ) => {
		const oldRemark = previousRequest?.PropertyCutoffProc?.Remarks;
		if ( oldRemark ) {
			return {
				notes: oldRemark,
			};
		}
		return {};
	};
	previousRequestId = ( previousRequest: ProcedureDetail ) => {
		return previousRequest?.PropertyCutoffProc?.Identity ?? undefined;
	};
	previousDocs = ( previousRequest: ProcedureDetail ) => {
		return previousRequest?.PropertyCutoffProc?.Documents ?? undefined;
	};
}

export interface AbstractRemarksFormProps extends BaseTabuRequestProps {
	initialState: ( previousRequest?: ProcedureDetail ) => Partial<State>;
	previousRequestId: ( previousRequest: ProcedureDetail ) => number | undefined;
	previousDocs: ( previousRequest: ProcedureDetail ) => TabuDocumentRequest[] | undefined;
	documentLookupName: () => string | undefined;
	procedure: ITabuSmartService;
	submitApi: <T extends OwnershipChangeRequest>( request: T ) => Promise<TabuRequestSubmitResponse>;
}

export class AbstractRemarksForm extends BaseTabuRequestForm<AbstractRemarksFormProps, State> {
	static contextType = TabuRequestContext;
	context!: React.ContextType<typeof TabuRequestContext>;

	constructor( props: AbstractRemarksFormProps ) {
		super( props );
		this.state = {
			errorContainer: {},
			docs: [],
		};
	}

	componentDidMount() {
		const { property, properties, reqDetails } = this.context;
		this.setState( {
			property: property,
			properties: properties,
			prevRequest: reqDetails,
			...this.props.initialState( reqDetails ),
		} as State, () => {
			if ( property || reqDetails )
				this.loadLookups();
		} );
	}

	render() {
		const { property, properties, reqDetails } = this.context;
		if ( property == null && reqDetails == null ) {
			return <Redirect to={ "/mydld" }/>;
		}
		const { procedure } = this.props;
		const isMultiProperty = isMultiPropertyProcedure( procedure.Id );
		const title = LocaleUtil.getLocalValue( procedure.Name );
		let headerTitle = title;
		if ( property ) {
			const { val, lbl } = extractPropInfo( property );
			headerTitle = `${ title } - ${ lbl } : ${ val }`;
		} else if ( reqDetails ) {
			const { label, value } = extractTabuRequestPropertyInfo( reqDetails );
			if ( label && value ) {
				headerTitle = `${ title } - ${ label } : ${ value }`;
			}
		}
		const { docs } = this.state;
		return <PageContent
			title={ headerTitle }
			hideQLinks={ true }>
			<PageTitle title={ title } paths={ [ DashboardBCItem ] }/>
			{ isMultiProperty && <RenderProperties
				property={ property! } properties={ properties }
				canEdit={ reqDetails == null } onEdit={ this.onEditProperties }/> }
			<div className="card">
				<div className="card-header">
					<span>{ title }</span>
				</div>
				<div className="card-body">
					<div className="mandatory-field-info">
						<small>{ Strings.mandatory_fields_note }</small>
					</div>
					<div className="row">
						<div className="col-xl-8 col-lg-8 col-md-8 col-sm-12 col-12">
							<TextInputField
								id={ "notes" }
								required={ true }
								holder={ this }
								label={ Strings.notes }
								errorProvider={ ErrorContainerStateProvider }
								maxLength={ 2000 }
								customInput={ <textarea rows={ 4 }/> }
								containerClassName={ "form-group field-style" }
								inputClassName={ "form-control" }/>
						</div>
					</div>

				</div>
				{ docs && docs.length > 0 &&
					<div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
						<RenderTabuRequiredDocuments
							reqDocuments={ docs }
							onDropRejected={ this.onDropRejected }
							onDrop={ this.onDrop }
							onDelete={ this.onDelete }/>
					</div>
				}
			</div>
			<div className="row">
				<div className="col-md-12 mt-3">
					<button
						className="btn btn_1 btn_style1" type="submit" id="btnContinue"
						onClick={ this.onContinue }>{ Strings.continue }</button>
					<a className="ms-3" onClick={ this.onReset }>{ Strings.reset }</a>
				</div>
			</div>
		</PageContent>;
	}

	getErrors = () => {
		return Object.values( this.state.errorContainer ).reduce( ( previousValue, currentValue ) => previousValue + "\n" + currentValue, "" ).trim();
	};

	private loadLookups() {
		let name = this.props.documentLookupName();
		if ( name ) {
			showLoading();
			TabuRequestApi.getLookup( name, ).then( TabuUtils.validateResponse )
			.then( ( data: Lookup[] ) => {
				let documentLookup = data.map( ToDocData );
				if ( this.state.prevRequest ) {
					const docs = fillDocumentsFromOldRequest( documentLookup, this.props.previousDocs( this.state.prevRequest ) );
					this.setState( { docs: docs } );
				} else {
					this.setState( { docs: documentLookup } );
				}

				hideLoading();
			} ).catch( ( reason ) => {
				hideLoading();
			} );
		}
	}

	private onDropRejected = () => {
		showErrorMessage( Strings.tabu_file_exceed_size );
	};

	private onDrop = ( doc: ReqTabuDoc, files: File[] ) => {
		if ( doc.MaxPages > 1 ) {
			if ( doc.docs && doc.docs.length > 0 ) {
				let maxAllowed = ( doc.MaxPages - doc.docs.length );
				if ( files.length > maxAllowed )
					files = files.splice( 0, maxAllowed );
			} else if ( files.length > doc.MaxPages ) {
				files = files.splice( 0, doc.MaxPages );
			}
		}
		files.forEach( ( file ) => {
			if ( file.type.startsWith( "image/" ) || file.type.startsWith( "application/pdf" ) ) {
				this.addFile( doc, file );
			}
		} );
	};

	private addFile( reqTabuDoc: ReqTabuDoc, file: File ) {
		const reader = new FileReader();
		reader.onload = () => {
			const binaryStr = reader.result;
			const tabuDoc: TabuDoc = {
				identity: reqTabuDoc.Identity,
				localData: binaryStr as string,
				fileName: file.name,
				fileSize: file.size,
				type: file.type,
			};
			if ( reqTabuDoc.MaxPages > 1 ) {
				if ( reqTabuDoc.docs == undefined ) {
					reqTabuDoc.docs = [];
				}
			} else {
				reqTabuDoc.docs = [];
			}
			reqTabuDoc.docs.push( tabuDoc );
			this.setState( {
				docs: this.state.docs.map( ( doc: ReqTabuDoc ) => {
					return ( reqTabuDoc.Identity === doc.Identity ) ? { ...doc, docs: reqTabuDoc.docs } : doc;
				} ),
			} );
		};
		reader.readAsDataURL( file );
	}

	private onDelete = ( identity: number, index: number ) => {
		this.setState( {
			docs: this.state.docs.map( ( reqTabuDoc: ReqTabuDoc ) => {
				if ( reqTabuDoc.Identity === identity ) {
					if ( reqTabuDoc.docs && reqTabuDoc.docs.length > 0 ) {
						reqTabuDoc.docs.splice( index, 1 );
					}
					return reqTabuDoc;
				}
				return reqTabuDoc;
			} ),
		} );
	};

	private onContinue = () => {
		if ( this.validateForm() ) {
			showLoading();
			this.uploadDocuments( this.state.docs ).then( ( docs: TabuDocumentRequest[] ) => {
				hideLoading();
				this.submitRequest( docs );
			} ).catch( ( reason ) => {
				hideLoading();
				showErrorMessage( reason.toString() );
			} );
		} else {
			this.setState( {}, () => {
				let error = this.getErrors();
				if ( !error || error === Strings.required )
					error = Strings.mandatory_fields_note;
				showErrorMessage( error );
			} );
		}
	};

	private onReset = () => {
		this.setState( {
			notes: "",
		} );
	};

	private validateForm() {
		const state = this.state;
		let isValid = true;
		let errorViews: ErrorContainer = {};
		isValid = checkNonEmpty( state, "notes", errorViews ) && isValid;
		if ( isValid ) {
			for ( const doc of this.state.docs ) {
				if ( doc.IsRequired && ( doc.docs == undefined || doc.docs.length === 0 ) )
					isValid = false;
			}
		}
		this.setState( { errorContainer: errorViews } );
		return isValid;
	}

	private submitRequest( docs: TabuDocumentRequest[] ) {
		showLoading();
		const state = this.state;
		const { procedure } = this.props;
		let { notes, property, properties, prevRequest } = state;
		const req: OwnershipChangeRequest = {
			ProcedureId: procedure.Id,
			ParticipantId: "" + getParticipantID(),
			AdditionalData: [
				{
					Key: "ParticipantId",
					Value: getParticipantID()!.toString(),
				},
				{
					Key: "RequestSource",
					Value: REQ_SOURCE_DLD_WEB,
				}
			],
			Remarks: notes ?? "",
			Documents: docs,
			Identity: prevRequest ? this.props.previousRequestId( prevRequest ) : 0,
		};
		if ( property ) {
			let allProperties = [ property ];
			if ( properties && properties.length > 0 ) {
				allProperties = [ property, ...properties ];
			}
			addAgentParticipantId( property, req );
			req.PropertyWithTypeIds = allProperties.map( x => {
				return { PropertyId: x.propertyId, PropertyTypeId: x.propertyTypeId };
			} );
		}
		trackEventWithData( "TabuProcedure", "TrySubmit", req );
		this.props.submitApi( req ).then( TabuUtils.validateResponse )
		.then( ( resp ) => {
			hideLoading();
			trackEventWithData( "TabuProcedure", "SubmitSuccess", {
				number: resp?.ProcedureNumber,
				year: resp?.ProcedureYear, ...req
			} );
			this.navigateToVoucher( this.props, resp );
		} )
		.catch( ( reason ) => {
			hideLoading();
			showErrorMessage( reason.toString() );
			trackEventWithData( "TabuProcedure", "SubmitFail", { reason: reason.toString(), ...req } );
		} );
	}

	private onEditProperties = () => {
		const { prevRequest } = this.state;
		if ( prevRequest ) {
			navigate( `/mydld/taburequest/${ prevRequest!.TabuRequest.ProcedureId }/properties` );
		} else {
			this.props.history.goBack();
		}
	};
}

function RenderProperties( props: {
	property: AssetCard, properties: AssetCard[], canEdit: boolean,
	onEdit: () => void
} ) {
	const { property, properties, canEdit, onEdit } = props;
	const typeID = Number( property.propertyTypeId );
	let allProperties = [ property ];
	if ( properties && properties.length > 0 ) {
		allProperties = [ property, ...properties ];
	}
	return <div className="row">
		<div className="col-xl-12 col-lg-12 col-md-12 col-sm-12 col-12">
			<div className="btn-alignment">
                <span className="alert alert-warning">
                    { `${ Strings.selected_properties } (${ allProperties.length })` }
                </span>
				{ canEdit && <button
					type="button" className="btn btn_1"
					onClick={ onEdit }>{ Strings.add_remove_properties }</button> }
			</div>
			<table className="table table-striped table-bordered dt-responsive nowrap font-size14">
				<thead>
				<tr>
					<th>{ Strings.property_type }</th>
					{ typeID === TabuPropertyType.LAND && <th>{ Strings.land_no }</th> }
					{ typeID === TabuPropertyType.UNIT && <th>{ Strings.Unit_Number_Label }</th> }
					{ typeID !== TabuPropertyType.LAND && <th>{ Strings.building_name }</th> }
					<th>{ Strings.area }</th>
				</tr>
				</thead>
				<tbody>
				{ allProperties.map( ( property, index ) => {
					const villaName = getVillaName( property );
					const buildingName = LocaleUtil.getLocalValue( ( property as any ).buildingName );
					const area = LocaleUtil.getLocalValue( property.area );
					return <tr key={ `req_property_${ index }` }>
						<td>{ getPropertyType( typeID ) }</td>
						{ typeID === TabuPropertyType.LAND && <td>{ getOPMunNo( property ) }</td> }
						{ typeID === TabuPropertyType.UNIT && <td>{ getUnitNumber( property ) }</td> }
						{ typeID !== TabuPropertyType.LAND && <td>{ buildingName ?? villaName }</td> }
						<td>{ area }</td>
					</tr>;
				} ) }
				</tbody>
			</table>
		</div>
	</div>;
}

export default OwnershipChangeForm;

function fillDocumentsFromOldRequest( docs: ReqTabuDoc[], Documents?: TabuDocumentRequest[] ): ReqTabuDoc[] {

	if ( Documents && Documents.length > 0 && docs && docs.length > 0 ) {

		for ( const doc of docs ) {
			const id = doc.Identity + "";
			const oldDocs = Documents.filter( x => x.DocumentId + "" === id );
			if ( oldDocs && oldDocs.length > 0 ) {
				doc.docs = [];
				for ( const oldDoc of oldDocs ) {
					for ( const dx of oldDoc.DocumentPath ) {
						doc.docs.push( { fileUrl: dx, identity: doc.Identity } as any as TabuDoc );
					}
				}
			}
		}
	}
	return docs;
}
